import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// ant design icons
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import InboxOutlined from '@ant-design/icons/InboxOutlined';

/**
 * Empty state for a Historical tab with nothing behind it.
 *
 * Separate from "still loading" on purpose — the hook only reports isEmpty once a
 * response has actually come back, so this never flashes before the first fetch.
 */
export function HistoryEmptyState({ message }) {
  const intl = useIntl();
  return (
    <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
      <InboxOutlined style={{ fontSize: '2rem', opacity: 0.35 }} />
      <Typography variant="body2" color="text.secondary">
        {message || intl.formatMessage({ id: 'history.noPreviousRecords', defaultMessage: 'No previous records' })}
      </Typography>
    </Stack>
  );
}

HistoryEmptyState.propTypes = { message: PropTypes.string };

/**
 * Prev/next control for a paged Historical tab.
 *
 * Renders nothing for a single page, so a sheet with three past sessions does not
 * grow a dead control. The count is spelled out ("6–10 of 26") because on a sheet
 * that loads five at a time, how much history exists behind the page is not
 * otherwise visible.
 */
export default function HistoryPagination({ page, totalPages, total, pageSize, onChange, loading = false }) {
  const intl = useIntl();
  if (!total || totalPages <= 1) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ pt: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        {intl.formatMessage({ id: 'history.showingRange', defaultMessage: 'Showing {from}–{to} of {total}' }, { from, to, total })}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          size="small"
          variant="outlined"
          startIcon={<LeftOutlined />}
          disabled={page <= 1 || loading}
          onClick={() => onChange(page - 1)}
        >
          {intl.formatMessage({ id: 'history.previous', defaultMessage: 'Previous' })}
        </Button>
        <Box component="span" sx={{ minWidth: 64, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {intl.formatMessage({ id: 'history.pageOf', defaultMessage: '{page} / {totalPages}' }, { page, totalPages })}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          endIcon={<RightOutlined />}
          disabled={page >= totalPages || loading}
          onClick={() => onChange(page + 1)}
        >
          {intl.formatMessage({ id: 'history.next', defaultMessage: 'Next' })}
        </Button>
      </Stack>
    </Stack>
  );
}

HistoryPagination.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool
};
